import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import {
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  userLogin,
  CoreLink,
  userGetCurrent,
  UI_OPERATION_STATUS_REASON_BLOCKED,
  UI_OPERATION_STATUS_REASON_UNRECOGNIZED,
  UI_OPERATION_STATUS_REASON_FLOOD,
} from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  FormTextField,
  Button,
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';
import { ROUTE_PASSWORD_REQUEST, ROUTE_ACCOUNT_EDIT } from '../../../config/routes';
import { clearPageInformation, setPageInformation, setPageViewSent } from '../../../store/actions/UI';
import LogoImageVNL from '../../../assets/images/VNL_logo_clean.svg';
import {
  APP_OPTIONS,
  DEFAULT_LANGUAGE,
  APPLICATON_VNL_ID,
  SSO_APPLICATON_RISK_MONITOR_ID,
} from '../../../config/constants';

const UserLogin = () => {
  const PAGE_TITLE = 'Inloggen | VeiligheidNL';
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const loggedIn = useSelector((state) => state.getIn(['login', 'loggedIn'], null));
  const userLoginStatus = useSelector((state) => state.getIn(['ui', 'user', 'operations', 'login', 'status']));
  const userLoginReason = useSelector((state) => state.getIn(['ui', 'user', 'operations', 'login', 'reason']));
  const urlParams = new URLSearchParams(window.location.search);
  const userParamValues = Object.fromEntries(urlParams);
  const userParamEmail = userParamValues.email;
  let redirectUrl = urlParams.get('destination') || urlParams.get('redirect_url');
  let activeApplication = APPLICATON_VNL_ID;
  if (APP_OPTIONS.SsoRiskMonitorClientIds.includes(urlParams.get('client_id'))) {
    activeApplication = SSO_APPLICATON_RISK_MONITOR_ID;
  }

  let reason;
  switch (userLoginReason) {
    case UI_OPERATION_STATUS_REASON_BLOCKED:
      reason = t('login.blocked');
      break;
    case UI_OPERATION_STATUS_REASON_FLOOD:
      reason = t('login.flood');
      break;
    case UI_OPERATION_STATUS_REASON_UNRECOGNIZED:
      reason = t('login.unrecognized');
      break;
    default:
      reason = t('login.unknown');
  }

  const onSubmit = (values, form) => {
    dispatch(userLogin(values.usernameLogin, values.passwordLogin));
    form.setSubmitting(false);
  };

  const goToRegister = useCallback(() => {
    const url = `/register${window.location.search}`;
    history.push(url);
  }, [history]);

  useEffect(() => {
    dispatch(userGetCurrent());
    dispatch(clearPageInformation());
    dispatch(setPageInformation(PAGE_TITLE));
    dispatch(setPageViewSent(false));
  }, []);

  useEffect(() => {
    if (loggedIn === true) {
      if (redirectUrl) {
        window.location = `${redirectUrl}${window.location.search}`;
      } else {
        history.push(ROUTE_ACCOUNT_EDIT);
      }
    }
  }, [history, dispatch, loggedIn]);

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: DEFAULT_LANGUAGE,
        }}
      >
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <Container small>
        <div className="FormPage PageLogin PageMinimal" id="main">
          <div className="PageMinimal__top">
            <CoreLink className="Logo" href="/">
              <img src={LogoImageVNL} alt="VeiligheidNL" />
            </CoreLink>
            <h1>{t(`login.${activeApplication}_title`)}</h1>
          </div>
          <div className="PageMinimal__content">
            {(userLoginStatus === UI_OPERATION_STATUS_ERROR) && (
              <div className="Form__error">
                {reason}
                {userLoginReason === UI_OPERATION_STATUS_REASON_BLOCKED && (
                  <CoreLink className="Form__error__customer_service" href="/klantenservice">
                    {t('general.customer_service')}
                  </CoreLink>
                )}
              </div>
            )}
            <FormikWrapper
              onSubmit={onSubmit}
              key={`login-form-${userParamEmail || 'default'}`}
              initialValues={{
                usernameLogin: userParamEmail || '',
              }}
            >
              <div className="PageLogin__username">
                <FormTextField
                  title={t('login.email.label')}
                  fieldName="usernameLogin"
                  required
                  disabled={userParamEmail}
                  autocomplete={userParamEmail ? 'off' : 'on'}
                />
              </div>
              <div className="PageLogin__password">
                <FormTextField
                  title={t('login.password.label')}
                  fieldName="passwordLogin"
                  showPasswordReveal
                  type="password"
                  className="InputPassword"
                  required
                />
              </div>
              <Button
                class="PasswordResetButton"
                className="PasswordResetButton"
                href={`${ROUTE_PASSWORD_REQUEST}${window.location.search}`}
              >
                {t('login.reset_password_button')}
              </Button>
              <div className="Form__actions">
                <Button
                  type="submit"
                  className={`Button Button--primary ${(userLoginStatus === UI_OPERATION_STATUS_REQUEST) ? 'Button--loading' : ''}`}
                >
                  {t('login.login_button')}
                  <i className="icon icon-arrow-right" />
                </Button>
                {!userParamValues.email && (
                  <a onClick={goToRegister} className="RegisterButton">
                    <span>{t('form.register.login_button')}</span>
                  </a>
                )}
              </div>
            </FormikWrapper>
          </div>
          <div className="PageMinimal__usp">
            <div className="PageMinimal__usp__link">
              <CoreLink href="/klantenservice" target="_blank" className="Button Button--secondary">
                {t('general.need_help')}
              </CoreLink>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default UserLogin;
